'use client';

import { useMemo } from 'react';
import { useAppTargetId } from '../store/projectStore';
import {
  buildDomainFiltersFromNodeMap,
  isDomainTreeFilterPending,
  resolveAssetSearchSources,
  ASSET_SEARCH_SOURCE,
  ASSET_VERIFIED,
  type AssetVerified,
} from '@terra/ui-design-system';
import { useDomainTreeFiltering } from '../contexts/SharedDomainTreeContext';
import { useAssetsSearch } from './api/useAssetsSearch';
import type { AssetPreviewDto } from '@terra/shared-contracts-assets';
import type { UseAssetsSearchOptions } from './api/useAssetsSearch';
import { ASSET_TYPE } from '../store/tab/assetsTabScopedFilterStore';

type EndpointAssetPreview = Extract<AssetPreviewDto, { assetType: 'endpoint' }>;
type WebPageAssetPreview = Extract<AssetPreviewDto, { assetType: 'webpage' }>;
type UnverifiedEndpointAssetPreview = Extract<
  AssetPreviewDto,
  { assetType: 'unverifiedEndpoint' }
>;

export type UseUnifiedAssetsDataOptions = Omit<
  UseAssetsSearchOptions,
  'applicationId' | 'domainFilters' | 'sources'
> &
  Required<Pick<UseAssetsSearchOptions, 'typeFilter' | 'methodsFilter'>> & {
    selectedNodes: string[];
    verified?: AssetVerified;
  };

export function useUnifiedAssetsData(options: UseUnifiedAssetsDataOptions) {
  const appTargetId = useAppTargetId();
  const {
    selectedNodes,
    verified = ASSET_VERIFIED.YES,
    ...searchOptions
  } = options;

  const { domainTree, isLoading: isDomainTreeLoading } =
    useDomainTreeFiltering('application');

  const nodeMap = domainTree?.nodeMap;
  const searchSources = useMemo(
    () =>
      resolveAssetSearchSources({
        verified,
        supportsUnverified: searchOptions.typeFilter === ASSET_TYPE.ENDPOINTS,
      }),
    [searchOptions.typeFilter, verified],
  );
  const includesVerifiedSource = searchSources.includes(
    ASSET_SEARCH_SOURCE.VERIFIED,
  );
  const isTreeFilterPending = includesVerifiedSource
    ? isDomainTreeFilterPending({
        selectedNodeIds: selectedNodes,
        domainTree,
      })
    : false;

  const domainFilters = useMemo(() => {
    if (!includesVerifiedSource || !nodeMap) {
      return undefined;
    }

    return buildDomainFiltersFromNodeMap(selectedNodes, nodeMap);
  }, [includesVerifiedSource, nodeMap, selectedNodes]);

  const assetsQuery = useAssetsSearch({
    ...searchOptions,
    sources: searchSources,
    applicationId: appTargetId,
    domainFilters,
    enabled: !isTreeFilterPending,
  });

  const assets = useMemo(() => {
    if (isTreeFilterPending) {
      return [];
    }

    return assetsQuery.data?.pages.flatMap((page) => page.assets) ?? [];
  }, [assetsQuery.data, isTreeFilterPending]);

  const totalCount = useMemo(() => {
    if (isTreeFilterPending) {
      return 0;
    }

    return assetsQuery.data?.pages[0]?.totalCount ?? assets.length;
  }, [assets, assetsQuery.data, isTreeFilterPending]);

  const groups = isTreeFilterPending
    ? undefined
    : assetsQuery.data?.pages[0]?.groups;

  const endpoints = useMemo(() => {
    return assets.filter(
      (asset): asset is EndpointAssetPreview => asset.assetType === 'endpoint',
    );
  }, [assets]);

  const webPages = useMemo(() => {
    return assets.filter(
      (asset): asset is WebPageAssetPreview => asset.assetType === 'webpage',
    );
  }, [assets]);

  const unverifiedEndpoints = useMemo(() => {
    return assets.filter(
      (asset): asset is UnverifiedEndpointAssetPreview =>
        asset.assetType === 'unverifiedEndpoint',
    );
  }, [assets]);

  return {
    assets,
    endpoints,
    webPages,
    unverifiedEndpoints,
    totalCount,
    groups,
    domainFilters,
    isLoading: isTreeFilterPending
      ? isDomainTreeLoading
      : assetsQuery.isLoading,
    isFetching: isTreeFilterPending
      ? isDomainTreeLoading
      : assetsQuery.isFetching,
    isFetchingNextPage: assetsQuery.isFetchingNextPage,
    hasNextPage: assetsQuery.hasNextPage ?? false,
    fetchNextPage: assetsQuery.fetchNextPage,
    refetch: assetsQuery.refetch,
    data: assetsQuery.data,
  };
}
